import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useEffect } from 'react';

import { Caret } from 'shared/icons/Caret';
import { Checkmark } from 'shared/icons/Checkmark';
import { ClientOnlyPortal } from 'shared/components/ClientOnlyPortal';
import { FeatureFlags } from 'featureFlags/constants';
import { FitFeedbackFormWindow } from 'productTemplate/components/fitFeedback/FitFeedbackFormWindow';
import { FitFeedbackFormWindow as FitFeedbackFormWindowDeprecated } from 'productTemplate/components/fitFeedback/FitFeedbackFormWindowDeprecated';
import { HoverDropdownMenu } from 'shared/components/HoverDropdownMenu';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setIsWantsWindowOpen } from 'store/slice/pdpSlice';
import { setWindowFocus } from 'store/slice/pdpStateSlice';
import { t } from 'localization/utils/t';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useFitFeedback } from 'productTemplate/hooks/useFitFeedback';
import { useIsProductTemplateWanted } from 'wants/hooks/useIsProductTemplateWanted';
import { useOffersContext } from 'productTemplate/hooks/useOffersContext';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useWindowState } from 'productTemplate/hooks/useWindowState';
import { variableFontRegular } from 'styles/theme/text';

const TP = 'productTemplate.components.footer.ProductTemplateDesktopFooterLinks';

const FactsWindow = dynamic(() => import('productTemplate/components/windows/FactsWindow'), {
  ssr: false,
});

const WantsWindow = dynamic(() => import('productTemplate/components/windows/WantsWindow'), {
  ssr: false,
});

const FitFeedbackWindowDeprecated = dynamic(
  () => import('productTemplate/components/fitFeedback/FitFeedbackWindowDeprecated'),
  {
    ssr: false,
  },
);

const FitFeedbackWindow = dynamic(
  () => import('productTemplate/components/fitFeedback/FitFeedbackWindow'),
  {
    ssr: false,
  },
);

const PricingActivityWindowDeprecated = dynamic(
  () => import('productTemplate/components/windows/PricingActivityWindowDeprecated'),
  { ssr: false },
);

const PricingActivityWindow = dynamic(
  () => import('productTemplate/components/windows/PricingActivityWindow'),
  { ssr: false },
);

const WINDOW_POSITION_OFFSET = 365;
// TODO: Remove this delay
// The amount of delay between closing all windows to reopening specific windows
const WINDOW_OPEN_DELAY = 50;

interface IProductTemplateDesktopFooterLinksProps {
  isFooterExpanded?: boolean;
  setIsFooterExpanded?: (val: boolean) => void;
}

export const ProductTemplateDesktopFooterLinks = ({
  isFooterExpanded,
  setIsFooterExpanded,
}: IProductTemplateDesktopFooterLinksProps) => {
  const { id, slug, productCategory, isOfferable, specialType, isResellable, isRaffleProduct } =
    useStoreSelector((state) => state.pdp.productTemplate);
  const { isWantsWindowOpen } = useStoreSelector((state) => state.pdp);
  const { isPlaceOfferModalShowing } = useOffersContext();
  const storeDispatch = useStoreDispatch();
  const isNewFitFeedbackEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_UPDATED_FIT_MODAL);
  const isNewPricingActivityEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ACTIVITY_MODAL);

  const showPricingActivity =
    isOfferable && specialType === 'standard' && isResellable && !isRaffleProduct;

  const {
    fitWindowState,
    handleFitClick,
    handleFitClose,
    handleFitMouseEnter,
    handleFitMouseLeave,
    isApparelFitFeedbackEnabled,
    isFitFeedbackFormWindowVisible,
    isSneakersFitFeedbackEnabled,
  } = useFitFeedback();

  const newCategories = ['collectibles', 'art', 'space'];
  const isFactsHidden = newCategories.includes(productCategory);

  const {
    windowSize: { width },
  } = useWindowSize();

  const {
    windowState: factsWindowState,
    handleLinkClick: handleFactsClick,
    handleLinkClose: handleFactsClose,
    handleLinkMouseEnter: handleFactsMouseEnter,
    handleLinkMouseLeave: handleFactsMouseLeave,
  } = useWindowState('facts', true);

  const {
    windowState: wantsWindowState,
    handleLinkClick: handleWantsClick,
    handleLinkClose: handleWantsClose,
    handleLinkMouseEnter: handleWantsMouseEnter,
    handleLinkMouseLeave: handleWantsMouseLeave,
  } = useWindowState('wants');

  const {
    windowState: pricingActivityWindowState,
    handleLinkClick: handlePricingActivityClick,
    handleLinkClose: handlePricingActivityClose,
    handleLinkMouseEnter: handlePricingActivityMouseEnter,
    handleLinkMouseLeave: handlePricingActivityMouseLeave,
  } = useWindowState('pricing');

  const showWantsCheckmark = useIsProductTemplateWanted();

  // Show wants modal based on sessionStorage's 'prevPath' and 'editWants'
  const handleWantsOpen = () => {
    const prevPath = window.sessionStorage.getItem('prevPath');
    const editWant = window.sessionStorage.getItem('editWant');

    // We are using 'editWant' as a "switch", it gets set when user click on ...
    // ... the edit CTA from profile wants and removed once we open the window
    if (editWant && prevPath === '/account/wants') {
      handleWantsClick();
      storeDispatch(setWindowFocus('wants')); // Puts wants on top
      window.sessionStorage.removeItem('editWant');
    }
  };

  const _handleWantsClose = () => {
    storeDispatch(setIsWantsWindowOpen(false));
    handleWantsClose();
  };

  // TODO: Replace with windowState when all windows are implemented
  const handleClick = (modalName: string, clickFunction: () => void) => {
    clickFunction();
    if (modalName) {
      sendTrackingEvent('MODAL_OPEN_VIEW', {
        product_template_slug: slug,
        modal_name: modalName,
      });
    }
  };

  // Listen for state in offers provider to show pricing modal
  useEffect(() => {
    if (isPlaceOfferModalShowing) {
      handlePricingActivityClick();
    }
  }, [isPlaceOfferModalShowing]);

  // Listen for redux state to open wants modal
  useEffect(() => {
    if (isWantsWindowOpen) {
      handleWantsClick();
    } else {
      handleWantsClose();
    }
  }, [isWantsWindowOpen]);

  // We close all windows when redirecting to another PT
  useEffect(() => {
    storeDispatch(setIsWantsWindowOpen(false));
    handleFitClose();
    handlePricingActivityClose();
  }, [id]);

  useEffect(() => {
    // Variant products will have an id as a query param to open the variant window
    const PDPIsVariant = window.location.search.includes('id=');

    let timer;
    if (!PDPIsVariant) {
      timer = setTimeout(() => handleWantsOpen(), WINDOW_OPEN_DELAY);
    }

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <ProductTemplateDesktopFooterLinks.Wrapper>
        {!isFactsHidden && (
          <button
            type="button"
            data-qa="product_template_facts"
            data-pdp-id="facts"
            className={`footer-link${factsWindowState.isOpen ? ' open' : ''}`}
            onClick={() => handleClick(null, handleFactsClick)}
            onMouseEnter={() => handleClick(null, handleFactsMouseEnter)}
            onMouseLeave={handleFactsMouseLeave}
          >
            {t(`${TP}.facts`, 'Facts')}
          </button>
        )}

        {(isSneakersFitFeedbackEnabled || isApparelFitFeedbackEnabled) && (
          <button
            type="button"
            data-qa="product_template_fit"
            data-pdp-id="fit"
            className={`footer-link${fitWindowState.isOpen ? ' open' : ''}`}
            onClick={() => handleClick('fit', handleFitClick)}
            onMouseEnter={() => handleClick('fit', handleFitMouseEnter)}
            onMouseLeave={handleFitMouseLeave}
          >
            {t(`${TP}.fit`, 'Fit')}
          </button>
        )}

        {showPricingActivity && (
          <button
            type="button"
            data-qa="product_template_pricing"
            data-pdp-id="pricing"
            className={`footer-link${pricingActivityWindowState.isOpen ? ' open' : ''}`}
            onClick={() => handleClick('pricingActivity', handlePricingActivityClick)}
            onMouseEnter={() => handleClick('pricingActivity', handlePricingActivityMouseEnter)}
            onMouseLeave={handlePricingActivityMouseLeave}
            onTouchStart={handlePricingActivityMouseEnter}
          >
            {t(`${TP}.activity`, 'Activity')}
          </button>
        )}

        <button
          type="button"
          data-qa="product_template_open_want"
          data-pdp-id="wants"
          className={`footer-link${wantsWindowState.isOpen ? ' open' : ''}`}
          onClick={() => handleClick('wants', handleWantsClick)}
          onMouseEnter={() => handleClick('wants', handleWantsMouseEnter)}
          onMouseLeave={handleWantsMouseLeave}
          onTouchStart={handleWantsMouseEnter}
        >
          {t(`${TP}.want`, 'Want')}
          {showWantsCheckmark && <Checkmark className="footer-checkmark" />}
        </button>

        {isFitFeedbackFormWindowVisible && (
          <ClientOnlyPortal selector="#main-page-layout">
            {isNewFitFeedbackEnabled ? (
              <FitFeedbackFormWindow />
            ) : (
              <FitFeedbackFormWindowDeprecated />
            )}
          </ClientOnlyPortal>
        )}
        <button
          type="button"
          className="footer-caret"
          onClick={() => setIsFooterExpanded(!isFooterExpanded)}
          aria-label="Expand Footer"
        >
          <Caret direction={isFooterExpanded ? 'down' : 'up'} />
        </button>
      </ProductTemplateDesktopFooterLinks.Wrapper>

      {factsWindowState.isOpen && (
        <FactsWindow
          initialWindowPosition={{ bottom: 0, left: width - WINDOW_POSITION_OFFSET }}
          isPermanent={factsWindowState.isPermanent}
          onClick={handleFactsClick}
          onClose={handleFactsClose}
        />
      )}
      {pricingActivityWindowState.isOpen &&
        (isNewPricingActivityEnabled ? (
          <PricingActivityWindow
            initialWindowPosition={{ bottom: 0, left: width - WINDOW_POSITION_OFFSET }}
            isHovering={pricingActivityWindowState.isHovering}
            isPermanent={pricingActivityWindowState.isPermanent}
            onClick={handlePricingActivityClick}
            onClose={handlePricingActivityClose}
          />
        ) : (
          <PricingActivityWindowDeprecated
            initialWindowPosition={{ bottom: 0, left: width - WINDOW_POSITION_OFFSET }}
            isHovering={pricingActivityWindowState.isHovering}
            isPermanent={pricingActivityWindowState.isPermanent}
            onClick={handlePricingActivityClick}
            onClose={handlePricingActivityClose}
          />
        ))}
      {fitWindowState.isOpen &&
        (isNewFitFeedbackEnabled ? (
          <FitFeedbackWindow
            initialWindowPosition={{ bottom: 0, left: width - WINDOW_POSITION_OFFSET }}
            isPermanent={fitWindowState.isPermanent}
            isHovering={fitWindowState.isHovering}
            onClick={handleFitClick}
            onClose={handleFitClose}
          />
        ) : (
          <FitFeedbackWindowDeprecated
            initialWindowPosition={{ bottom: 0, left: width - WINDOW_POSITION_OFFSET }}
            isPermanent={fitWindowState.isPermanent}
            isHovering={fitWindowState.isHovering}
            onClick={handleFitClick}
            onClose={handleFitClose}
          />
        ))}
      {wantsWindowState.isOpen && (
        <WantsWindow
          initialWindowPosition={{ bottom: 0, left: width - WINDOW_POSITION_OFFSET }}
          isPermanent={wantsWindowState.isPermanent}
          isHovering={wantsWindowState.isHovering}
          onClick={handleWantsClick}
          onClose={_handleWantsClose}
        />
      )}
    </>
  );
};

ProductTemplateDesktopFooterLinks.Wrapper = styled.div`
  align-items: flex-start;
  background-color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex: 1;
  flex-direction: row;
  height: 100%;
  justify-content: flex-end;
  z-index: ${({ theme }) => theme.zIndex.pdpFooterItem};

  ${HoverDropdownMenu.Wrapper} {
    position: initial;
  }

  ${HoverDropdownMenu.DropdownMenu} {
    position: absolute;
    left: calc(100vw - ${WINDOW_POSITION_OFFSET}px);
  }

  .footer-caret {
    cursor: pointer;
    padding: 15px 16px 0 0;
    width: 51px;

    text-align: left;
  }

  .footer-checkmark {
    margin: 0 0 2px 4px;
  }

  .footer-link {
    margin-right: 16px;
    padding-top: 15px;
    width: fit-content;

    ${variableFontRegular({ height: '14px', size: '11.5px' })}

    &:hover {
      text-decoration: underline;
    }

    &.open {
      text-decoration: underline;
    }
  }
`;
